import { FC } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { AssessmentDomainDetail, PreviousDomainSummary } from '@libs/types';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react';

import { DomainSummaryItem } from './DomainSummaryItem';

interface DomainSummaryListProps {
  assessmentId: string;
  domains: AssessmentDomainDetail[];
  /**
   * Parent-version snapshots keyed by frameworkDomain orderIndex. Empty map
   * for v1 assessments. Each entry, when present, drives the per-row
   * comparison UI (parent score, missing-signal pill, removed docs).
   */
  previousByOrderIndex?: Map<number, PreviousDomainSummary>;
  /** Parent assessment version number, e.g. 3 for "From V3". `undefined` for v1. */
  parentVersion?: number;
  onDownloadDocument: (documentId: string) => void;
  onSubmitClick: () => void;
}

export const DomainSummaryList: FC<DomainSummaryListProps> = observer(
  ({ assessmentId, domains, previousByOrderIndex, parentVersion, onDownloadDocument, onSubmitClick }) => {
    return (
      <Paper variant="panel" elevation={0} >
        {/* Title */}
        <Typography variant="textLG_medium" sx={{ mb: 0.5 }}>
          <Trans i18nKey="assessments.summary.domainSummary" />
        </Typography>

        {/* Domains list */}
        <Box

          sx={{
            border: "none",
          }}
        >
          {domains.map((domain) => {
            const orderIndex = domain.frameworkDomain?.orderIndex;
            const previousDomainSummary =
              orderIndex !== undefined ? previousByOrderIndex?.get(orderIndex) : undefined;
            return (
              <DomainSummaryItem
                key={domain.id}
                assessmentId={assessmentId}
                domain={domain}
                previousDomainSummary={previousDomainSummary}
                parentVersion={parentVersion}
                onDownloadDocument={onDownloadDocument}
              />
            );
          })}
        </Box>

        {/* Submit button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="primary"
            onClick={onSubmitClick}
            endIcon={<PaperPlaneTiltIcon size={20} />}
            sx={{
              px: 5,
              py: 1.5,
              gap: 1,
            }}
          >
            <Typography variant="textMD_semiBold" sx={{ color: 'white' }}>
              <Trans i18nKey="assessments.summary.submitAssessment" />
            </Typography>
          </Button>
        </Box>
      </Paper>
    );
  },
);
